import { createRef, useMemo, useState } from 'react';

import { HitType } from '@/modules/common/lib/abtastyStub';

import { useAddToCart } from '@/modules/cart/hooks/useAddToCart';
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import { Heart } from '@/modules/common/components/icons';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import { useQueryParams } from '@/modules/common/hooks/useQueryParams';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { SamplePrice } from '@/modules/product/components/sample/SamplePrice';
import { useVisibleItemsTracking } from '@/modules/product/hooks/useVisibleItemsTracking';
import { ContentfulCollection } from '@/modules/product/models/collection.interface';
import { ProductPdpInterface } from '@/modules/product/models/product-pdp.interface';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '@/modules/product/services/data-layer-events/utils';
import { selectStatus } from '@/modules/register/state/registerSlice';
import { SampleCard } from '@/modules/sample-card';
import {
  ShoppingListSaveType,
  useShoppingListActions,
} from '@/modules/shopping-list/hooks/useShoppingListActions';
import { selectShoppingListStatus } from '@/modules/shopping-list/state/shoppingListSlice';

import { useSampleCardVariants } from '../../hooks/useSampleCardVariants';

type SelectedProduct = {
  sku: string;
  color?: string;
  image?: {
    url?: string | null;
    label?: string | null;
  }[];
  price: number;
  productId: string;
  productName: string;
};

type CollectionListGridDesigner = {
  products: ProductPdpInterface[];
  collection: ContentfulCollection;
  exactMatches?: string[];
};
export const CollectionListGridDesigner = ({
  products,
  exactMatches = [],
  collection,
}: CollectionListGridDesigner) => {
  const { sendFeatureFlagTracking } = useFeatureFlag();

  const [activeCategory] = useQueryParams<string>('c', 'All');
  const [sortPrice] = useQueryParams<string>('sort', '');
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const cartStatus = useAppSelector(selectStatus);
  const shoppingListStatus = useAppSelector(selectShoppingListStatus);
  const applied_filters = useMemo(() => {
    return [{ type: 'Category', value: activeCategory || 'All' }];
  }, [activeCategory]);
  const { enhancedProducts } = useSampleCardVariants(products);

  const { bulkAddProductsToShoppingList } = useShoppingListActions({
    bulkProducts: selectedProducts,
    type: ShoppingListSaveType.Product,
  });

  const totalPrice = selectedProducts.reduce((acc, product) => acc + product.price, 0);

  // Create a filtered products array that reflects what's actually displayed
  const filteredProducts = useMemo(() => {
    return enhancedProducts
      .filter((product) => {
        if (activeCategory === 'All') {
          return true;
        }

        const categoriesAvailable = product?.category?.name.split(' > ') || [];
        return categoriesAvailable.some((category) => category === activeCategory);
      })
      .sort((a, b) => {
        if (!sortPrice) {
          return 0;
        }
        const aPrice = a.samplePrice;
        const bPrice = b.samplePrice;
        if (sortPrice === 'asc') {
          return aPrice - bPrice;
        }
        return bPrice - aPrice;
      });
  }, [enhancedProducts, activeCategory, sortPrice]);

  const productRefs = useMemo(
    () => filteredProducts.map(() => createRef<HTMLDivElement>()),
    [filteredProducts]
  );

  useVisibleItemsTracking({
    products: filteredProducts,
    productRefs,
    collection,
    shoppingListProducts: [],
    feature: DataLayerFeature.DesignerCollection,
    where: DataLayerWhere.Collections,
    itemListId: collection.slug ?? '',
    itemListName: collection.collectionName ?? '',
    itemListType: 'Designer collection',
    applied_filters,
  });

  const handleCheckProduct = (checked: boolean, sku: string) => {
    if (checked) {
      setSelectedProducts((prev) => {
        const product = products.find((product) => product.sku === sku);
        if (product) {
          return [
            ...prev,
            {
              sku,
              color: product.color || '',
              image:
                product.images?.map((image) => ({
                  url: image.url,
                  label: image.label,
                })) || [],
              price: product.samplePrice || 0,
              productId: product.id,
              productName: product.name || '',
            },
          ];
        }
        return prev;
      });
    } else {
      setSelectedProducts((prev) => prev.filter((product) => product.sku !== sku));
    }
  };

  const handleAddToWishlist = async () => {
    await bulkAddProductsToShoppingList(false);
    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      where: DataLayerWhere.ProductCard,
      method: 'Multi-select',
      type: ShoppingListSaveType.Collection,
      shoppingListProducts: null,
      product: {
        slug: collection.slug ?? '',
        title: collection.collectionName ?? '',
      } satisfies CollectionDataLayer,
      item_list_type: 'Editorial collection',
      item_list_name: collection.collectionName ?? '',
      item_list_id: collection.slug ?? '',
      applied_filters,
    });
    setSelectedProducts([]);

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Product Saved',
      label: collection.collectionName,
    });
  };

  const { onAddToCartBulk } = useAddToCart({
    where: DataLayerWhere.Collections,
    appliedFilters: applied_filters,
  });

  const handleAddProductsToCart = async () => {
    const productsToAdd = products.filter((product) =>
      selectedProducts.some((selectedProduct) => selectedProduct.sku === product.sku)
    );

    onAddToCartBulk(
      productsToAdd.map((product) => ({
        ...product,
        exactMatch: exactMatches?.includes(product?.sku || ''),
      }))
    );
    setSelectedProducts([]);
  };

  if (products.length === 0) {
    return <div className="px-8">No products</div>;
  }

  return (
    <div
      className={`grid h-fit grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4`}
    >
      {filteredProducts.map((product, index) => {
        return (
          <div
            key={product.sku}
            ref={productRefs[index]}
            data-product-id={product.id}
            data-index={index}
            className="flex flex-col"
          >
            <SampleCard
              index={index}
              key={product.sku}
              appliedFilters={applied_filters}
              data-testid="sample-card"
              feature={DataLayerFeature.DesignerCollection}
              isSelected={selectedProducts.some(
                (selectedProduct) => selectedProduct.sku === product?.sku
              )}
              isSelectable={product.isOnStock}
              item_list_type="Designer collection"
              onCheck={(checked: boolean) => handleCheckProduct(checked, product?.sku || '')}
              viewMode="compact"
              where={DataLayerWhere.ProductCard}
              {...product}
              {...(collection.collectionName && {
                item_list_name: collection.collectionName,
              })}
              {...(collection.slug && {
                item_list_id: collection.slug,
                hrefQueryParams: {
                  collection: collection.slug,
                },
              })}
            />
          </div>
        );
      })}

      <div
        className={`animate-fade-in-up visible fixed bottom-5 left-0 right-0 z-10 mx-auto flex w-fit items-center gap-3 rounded-xxl border border-gray-200 bg-white p-4 opacity-100 transition-all duration-100 ease-in-out ${selectedProducts.length === 0 ? 'invisible translate-y-full opacity-0' : ''} `}
      >
        <Button
          className="py-3 max-lg:p-3"
          variant="secondary"
          onClick={handleAddToWishlist}
          isLoading={shoppingListStatus === 'loading'}
          data-testid="add-to-wishlist-button"
        >
          <span className="hidden gap-2.5 lg:flex">
            <Heart size="small" /> Save
          </span>
          <span className="lg:hidden">
            <Heart />
          </span>
        </Button>
        <Button
          className="relative flex gap-2"
          onClick={handleAddProductsToCart}
          isLoading={cartStatus === 'loading'}
          data-testid="add-to-cart-button"
        >
          {selectedProducts
            .slice(0, 3)
            .map(
              (product, index) =>
                product?.image?.[0]?.url && (
                  <Image
                    key={product.sku}
                    src={product.image[0].url}
                    alt="product"
                    className={`size-[25px] rounded-md border border-transparent z-[${index}] ${index !== 0 ? '-ml-5 border-black' : ''} `}
                    width={25}
                    height={25}
                  />
                )
            )}{' '}
          Add to cart <SamplePrice priceInCents={totalPrice} />
        </Button>
      </div>
    </div>
  );
};
